import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Globe, Save } from 'lucide-react';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import type { ProductWorkspaceDetail } from '../productLabelTypes';

countries.registerLocale(enLocale);

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type Props = {
  productId: string | null;
  onClose: () => void;
  onEdit: (product: ProductWorkspaceDetail) => void;
  onOpenLabel: (product: ProductWorkspaceDetail) => void;
  onProductUpdated?: () => void;
  authorizedFetch: (url: string, init?: RequestInit) => Promise<Response>;
  formatPrice: (n: number) => string;
  currency: string;
};

function DetailField({
  label,
  value,
  className = '',
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  const display =
    value === null || value === undefined || value === '' ? (
      <span className="text-slate-400">—</span>
    ) : (
      value
    );
  return (
    <div className={className}>
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-800">{display}</dd>
    </div>
  );
}

function formatMoney(
  value: number | string | null | undefined,
  currency: string,
  formatPrice: (n: number) => string,
) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${currency} ${formatPrice(n)}`;
}

export default function ProductDetailsDrawer({
  productId,
  onClose,
  onEdit,
  onOpenLabel,
  onProductUpdated,
  authorizedFetch,
  formatPrice,
  currency,
}: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState<ProductWorkspaceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [salePriceInput, setSalePriceInput] = useState('');
  const [savingSalePrice, setSavingSalePrice] = useState(false);
  const [salePriceMessage, setSalePriceMessage] = useState<string | null>(null);

  const loadProduct = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/catalog/products/${productId}/workspace`);
      if (!res.ok) throw new Error('load');
      const data = (await res.json()) as ProductWorkspaceDetail;
      setProduct(data);
      const sale = data.salePrice ?? data.pricePerUnit;
      setSalePriceInput(
        sale != null && Number(sale) > 0 ? String(Number(sale)) : '',
      );
      setSalePriceMessage(null);
    } catch {
      setError('Unable to load product details');
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, productId]);

  useEffect(() => {
    if (!productId) {
      setProduct(null);
      return;
    }
    void loadProduct();
  }, [loadProduct, productId]);

  const saveSalePrice = async () => {
    if (!product) return;
    setSavingSalePrice(true);
    setSalePriceMessage(null);
    setError(null);
    try {
      const parsed = salePriceInput.trim() === '' ? 0 : Number(salePriceInput);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setSalePriceMessage('Enter a valid sale price');
        return;
      }
      const res = await authorizedFetch(
        `${API_BASE}/catalog/products/${product.id}/sale-price`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ salePrice: parsed > 0 ? parsed : null }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || 'save failed');
      }
      setSalePriceMessage('Sale price saved');
      await loadProduct();
      onProductUpdated?.();
    } catch (e) {
      setSalePriceMessage(e instanceof Error ? e.message : 'Unable to save sale price');
    } finally {
      setSavingSalePrice(false);
    }
  };

  if (!productId) return null;

  const originName = product?.countryOfOrigin
    ? countries.getName(product.countryOfOrigin.toUpperCase(), 'en') || product.countryOfOrigin
    : null;
  const unit = product?.unitLabel || product?.baseUnit || 'unit';
  const discount = Number(product?.discountPercent ?? 0);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40">
      <button type="button" className="flex-1" aria-label="Close" onClick={onClose} />
      <aside className="flex h-full w-full max-w-lg flex-col border-l border-slate-200 bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-900">
            {t('backoffice.products_details_title', { defaultValue: 'Product details' })}
          </h2>
          <button type="button" onClick={onClose} className="border border-slate-300 p-1.5 text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <p className="text-sm text-slate-600">{t('common.loading')}</p>
          ) : error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : product ? (
            <div className="space-y-5">
              <div className="flex gap-3">
                {product.imageUrl ? (
                  <img src={product.imageUrl} alt="" className="h-20 w-20 border border-slate-200 object-cover" />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center border border-dashed border-slate-300 bg-slate-50 text-xs text-slate-400">
                    No image
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-slate-900">{product.name}</h3>
                  <p className="text-xs text-slate-500">{product.sku}</p>
                  <span
                    className={
                      product.isActive
                        ? 'mt-1 inline-block border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700'
                        : 'mt-1 inline-block border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-rose-700'
                    }
                  >
                    {product.isActive ? 'Active' : 'Disabled'}
                  </span>
                </div>
              </div>

              <section>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Catalog
                </h4>
                <dl className="grid grid-cols-2 gap-3">
                  <DetailField label="SKU" value={product.sku} />
                  <DetailField label="Name" value={product.name} className="sm:col-span-1" />
                  <DetailField label="Category" value={product.category?.name} />
                  <DetailField
                    label="Department"
                    value={product.category?.department?.name}
                  />
                  <DetailField label="Base unit" value={product.baseUnit} />
                  <DetailField label="Unit label" value={product.unitLabel} />
                  <DetailField label="Status" value={product.isActive ? 'Active' : 'Disabled'} />
                </dl>
              </section>

              <section className="border border-slate-200 bg-slate-50 p-3">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Pricing
                </h4>
                <dl className="mb-3 grid grid-cols-2 gap-3">
                  <DetailField
                    label="Purchase price / unit"
                    value={formatMoney(product.purchasePricePerUnit, currency, formatPrice)}
                  />
                  <DetailField
                    label="List price / unit"
                    value={formatMoney(product.pricePerUnit, currency, formatPrice)}
                  />
                  <DetailField
                    label="Discount"
                    value={discount > 0 ? `${discount.toFixed(2)}%` : null}
                  />
                  <DetailField
                    label="Fixed delivery fee"
                    value={formatMoney(product.fixedDeliveryFee, currency, formatPrice)}
                  />
                </dl>
                <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Sale price / {unit}
                </label>
                <div className="mt-1 flex gap-2">
                  <div className="relative flex-1">
                    <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                      {currency}
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full border border-slate-300 py-2 pl-10 pr-2 text-sm"
                      value={salePriceInput}
                      onChange={(e) => setSalePriceInput(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <button
                    type="button"
                    disabled={savingSalePrice}
                    onClick={() => void saveSalePrice()}
                    className="inline-flex items-center gap-1 border border-slate-800 bg-slate-800 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
                  >
                    <Save className="h-3.5 w-3.5" />
                    {savingSalePrice ? 'Saving…' : 'Save'}
                  </button>
                </div>
                {salePriceMessage ? (
                  <p
                    className={`mt-1.5 text-xs ${
                      salePriceMessage.includes('saved') ? 'text-emerald-700' : 'text-red-600'
                    }`}
                  >
                    {salePriceMessage}
                  </p>
                ) : null}
                <p className="mt-2 text-[11px] text-slate-500">
                  Sale price is what customers pay. Required before enabling the product.
                </p>
              </section>

              <section>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Inventory
                </h4>
                <dl className="grid grid-cols-2 gap-3">
                  <DetailField
                    label="Stock (lb)"
                    value={Number(product.currentStockLbs).toFixed(2)}
                  />
                  <DetailField
                    label="Typical expiry (days)"
                    value={
                      product.typicalExpiryDays != null
                        ? `${product.typicalExpiryDays.toFixed(0)}d`
                        : null
                    }
                  />
                </dl>
              </section>

              <section>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Content
                </h4>
                <dl className="space-y-3">
                  <DetailField label="Description" value={product.description} />
                  <DetailField label="Benefits" value={product.benefits} />
                </dl>
              </section>

              <section>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Pre-order
                </h4>
                <dl className="grid grid-cols-2 gap-3">
                  <DetailField
                    label="Pre-order"
                    value={product.isPreorderEnabled ? 'Enabled' : 'Disabled'}
                  />
                  <DetailField
                    label="ETA"
                    value={
                      product.preorderEta
                        ? String(product.preorderEta).slice(0, 10)
                        : null
                    }
                  />
                  <DetailField
                    label="Deposit"
                    value={
                      product.preorderDepositValue != null
                        ? `${product.preorderDepositValue} (${product.preorderDepositType || '—'})`
                        : null
                    }
                  />
                  <DetailField
                    label="Cancellation fee"
                    value={
                      product.preorderCancellationFeeValue != null
                        ? `${product.preorderCancellationFeeValue} (${product.preorderCancellationFeeType || '—'})`
                        : null
                    }
                  />
                  <DetailField
                    label="Max qty (lb)"
                    value={
                      product.preorderMaxQtyLbs != null
                        ? Number(product.preorderMaxQtyLbs).toFixed(2)
                        : null
                    }
                  />
                </dl>
              </section>

              <section className="border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  <Globe className="h-3.5 w-3.5" />
                  International shipping
                </div>
                <dl className="grid grid-cols-1 gap-2">
                  <DetailField label="HS code" value={product.hsCode} />
                  <DetailField
                    label="Country of origin"
                    value={
                      product.countryOfOrigin
                        ? `${originName} (${product.countryOfOrigin})`
                        : null
                    }
                  />
                </dl>
              </section>

              <section>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Sellable packages
                </h4>
                {product.packagingOptions && product.packagingOptions.length > 0 ? (
                  <ul className="space-y-1 border border-slate-200 bg-white p-2 text-sm text-slate-800">
                    {product.packagingOptions.map((opt) => (
                      <li key={opt.id}>
                        {opt.label} — {opt.sizeLbs} lb
                        {opt.isDefault ? ' (default)' : ''}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-400">—</p>
                )}
              </section>

              <section>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Label template
                </h4>
                <DetailField
                  label="Saved label size"
                  value={
                    product.labelDesign
                      ? `${product.labelDesign.widthIn}″ × ${product.labelDesign.heightIn}″`
                      : null
                  }
                />
              </section>
            </div>
          ) : null}
        </div>
        {product ? (
          <footer className="flex flex-wrap gap-2 border-t border-slate-200 p-4">
            <button
              type="button"
              onClick={() => onEdit(product)}
              className="border border-slate-800 bg-slate-800 px-3 py-2 text-xs font-medium text-white"
            >
              {t('backoffice.products_action_edit', 'Edit full form')}
            </button>
            <button
              type="button"
              onClick={() => onOpenLabel(product)}
              className="border border-sky-700 bg-sky-700 px-3 py-2 text-xs font-medium text-white"
            >
              {t('backoffice.products_label_workspace', { defaultValue: 'Label workspace' })}
            </button>
          </footer>
        ) : null}
      </aside>
    </div>
  );
}
